// Text Sanitization
// -----------------
// Shared normalization for user-entered FREE-TEXT fields (display names,
// web-app names, test-account usernames, ...). Mirrors the backend
// nimis-intelligence-backend/models/text_sanitization.py::normalize_free_text
// so the two agree exactly.
//
// ROOT-CAUSE INTENT (see SECURITY_AUDIT_FINDINGS F4-F7)
// ----------------------------------------------------
// Free-text evidence must be stored FAITHFULLY — a value may legitimately
// contain `<script>` or a SQL payload. We do NOT strip HTML or reject
// content; the XSS defense is context-correct OUTPUT ENCODING (React JSX
// escaping) at render time. This function performs only bounded,
// non-destructive hygiene, and the backend re-applies it authoritatively:
//   - Unicode NFC normalization.
//   - Strip C0/C1 control characters and DEL, keeping tab/newline/CR.
//   - Trim surrounding whitespace.
//   - Enforce a maximum length (counted in Unicode code points).

// Control characters preserved during normalization. Every other character
// below 0x20, plus DEL (0x7f) and the C1 range (0x80-0x9f), is stripped.
const ALLOWED_CONTROL_CHARACTERS: ReadonlySet<string> = new Set(["\t", "\n", "\r"]);

function isRetainedCharacter(character: string): boolean {
  if (ALLOWED_CONTROL_CHARACTERS.has(character)) {
    return true;
  }
  const codePoint = character.codePointAt(0) ?? 0;
  // Drop C0 controls (<0x20), DEL (0x7f) and C1 controls (0x80-0x9f).
  if (codePoint < 0x20 || codePoint === 0x7f || (codePoint >= 0x80 && codePoint <= 0x9f)) {
    return false;
  }
  return true;
}

// Returns NFC-normalized text with disallowed control characters stripped,
// surrounding whitespace trimmed, and length capped. Content is preserved
// (HTML/quotes/SQL are NOT removed). Returns "" for empty/non-string input.
// `maxLength` counts Unicode code points; omit for no cap.
export function normalizeFreeText(
  value: string | null | undefined,
  maxLength?: number,
): string {
  if (!value || typeof value !== "string") {
    return "";
  }

  const normalized = value.normalize("NFC");
  let cleaned = "";
  // for..of iterates by code point, so surrogate pairs stay intact.
  for (const character of normalized) {
    if (isRetainedCharacter(character)) {
      cleaned += character;
    }
  }
  cleaned = cleaned.trim();

  if (typeof maxLength === "number") {
    // Count/slice by code point to match the backend's len() semantics.
    const codePoints = Array.from(cleaned);
    if (codePoints.length > maxLength) {
      cleaned = codePoints.slice(0, maxLength).join("");
    }
  }

  return cleaned;
}

// Validate a CREDENTIAL field (test-account username / password / TOTP
// secret) WITHOUT mutating it. Returns a human-readable error message, or
// null if valid. Mirrors the backend validate_credential_field.
//
// Credentials must be used EXACTLY as entered — silently trimming or
// rewriting one breaks the scan login in a way that is not obvious to the
// user (SECURITY_AUDIT_FINDINGS F6). So this only rejects control
// characters (tab/newline/CR are NOT exempt here) and over-length values;
// whitespace and printable symbols are preserved. Empty is treated as "not
// provided".
export function validateCredentialField(
  fieldLabel: string,
  value: string | null | undefined,
  maxLength: number,
): string | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  if (typeof value !== "string") {
    return `${fieldLabel} must be text`;
  }
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint < 0x20 || codePoint === 0x7f || (codePoint >= 0x80 && codePoint <= 0x9f)) {
      return `${fieldLabel} must not contain control characters`;
    }
  }
  if (Array.from(value).length > maxLength) {
    return `${fieldLabel} must be at most ${maxLength} characters`;
  }
  return null;
}

// True when a value has leading or trailing whitespace. Used to visibly
// WARN about a credential that will be saved with surrounding spaces —
// rather than silently trimming it (SECURITY_AUDIT_FINDINGS F6). Non-string
// / empty values have none.
export function hasSurroundingWhitespace(value: string | null | undefined): boolean {
  if (!value || typeof value !== "string") {
    return false;
  }
  return value !== value.trim();
}
